import { Button, TButtonProps } from '@populix/pop-ui'
import { createPortal } from 'react-dom'
import {
  MdCheckCircleOutline,
  MdClose,
  MdErrorOutline,
  MdInfoOutline,
  MdWarningAmber,
} from 'react-icons/md'

export type TToastDialogType = 'success' | 'warning' | 'error' | 'info'

export type TToastDialog = {
  readonly id?: string
  readonly show: boolean
  readonly positionBottom?: boolean
  readonly type: TToastDialogType
  readonly title: string
  readonly description?: string
  readonly traceId?: string
  readonly disableClose?: boolean
  readonly onClose?: () => void
  readonly primaryButtonTitle?: string
  readonly secondaryButtonTitle?: string
  readonly onClickPrimaryButton?: () => void
  readonly onClickSecondaryButton?: () => void
  readonly disableIcon?: boolean
}

export default function ToastDialog({
  id,
  show,
  positionBottom,
  type = 'success',
  title,
  description,
  traceId,
  disableClose,
  onClose,
  primaryButtonTitle,
  secondaryButtonTitle,
  onClickPrimaryButton,
  onClickSecondaryButton,
  disableIcon,
}: TToastDialog) {
  const typeClass = {
    success: 'border-pop-green-500 bg-pop-green-50',
    warning: 'border-pop-yellow-200 bg-pop-yellow-50',
    error: 'border-pop-red-500 bg-pop-red-50',
    info: 'border-pop-blue-500 bg-pop-blue-50 text-pop-blue-700',
  }[type]

  const iconType = {
    success: <MdCheckCircleOutline className='my-auto h-6 w-6 text-pop-green-600' />,
    warning: <MdWarningAmber className='my-auto h-6 w-6 text-pop-yellow-300' />,
    error: <MdErrorOutline className='my-auto h-6 w-6 text-pop-red-500' />,
    info: <MdInfoOutline className='my-auto h-6 w-6 text-pop-blue-600' />,
  }[type]

  const buttonTheme: { [key in TToastDialogType]: TButtonProps['color'] } = {
    success: 'success',
    warning: 'secondary',
    error: 'error',
    info: 'primary',
  }

  return createPortal(
    <div
      id={`toast_${id}`}
      data-testid='toast-container'
      className={`${
        positionBottom
          ? 'fixed bottom-0 left-0 right-0 mx-auto flex flex-col gap-4 px-5 py-4 md:w-full'
          : ''
      }`}
    >
      <div
        className={`${
          show ? 'flex' : 'hidden'
        } flex-row gap-3 rounded-2xl border ${typeClass} px-4 py-3 shadow-lg md:px-5 md:py-2`}
      >
        {!disableIcon && iconType}
        <figure className='flex w-full flex-col justify-between md:flex-row'>
          <div className='flex flex-row items-start justify-between'>
            <div className='my-auto flex flex-col items-start justify-center'>
              <figcaption className='text-base font-medium'>{title}</figcaption>
              {!!description && <p className='text-sm font-light'>{description}</p>}
              {!!traceId && (
                <p className='mt-2 text-xs font-light text-pop-grey-500'>Trace ID: {traceId}</p>
              )}
            </div>
            {disableClose ? null : (
              <MdClose
                className='ml-auto flex h-6 w-6 cursor-pointer text-pop-grey-600 md:hidden'
                onClick={onClose}
              />
            )}
          </div>
          <div className='mt-3 flex flex-row-reverse items-center justify-center gap-3 md:mt-0 lg:flex-row'>
            {!!primaryButtonTitle && (
              <Button variant='contained' color={buttonTheme[type]} onClick={onClickPrimaryButton}>
                <span className='text-nowrap px-2'>{primaryButtonTitle}</span>
              </Button>
            )}
            {!!secondaryButtonTitle && (
              <Button variant='text' color={buttonTheme[type]} onClick={onClickSecondaryButton}>
                <span className='text-nowrap px-2'>{secondaryButtonTitle}</span>
              </Button>
            )}
          </div>
        </figure>
        {disableClose ? null : (
          <MdClose
            className='my-auto ml-auto hidden h-6 w-6 cursor-pointer text-pop-grey-600 md:flex'
            onClick={onClose}
            data-testid='toast-close-button'
          />
        )}
      </div>
    </div>,
    document.body,
  )
}
